import React from 'react';
import { useTranslation } from 'react-i18next';

import { NotesStack } from 'icons/untitledui';

import colours from 'theme/atoms/colours';
import * as Buttons from 'theme/components/Buttons.style';
import * as Text from 'theme/components/Text.style';
import * as Styled from 'priorities/styles/PriorityCard.style';

export type PriorityCardVariant = 'routine' | 'updateEvent';

export interface PriorityCardProps {
  variant: PriorityCardVariant;
  prompt: string;
  previousUpdate?: string;
  onRecord: () => void;
  onClick?: () => void;
}

export default function PriorityCard({
  variant,
  prompt,
  previousUpdate,
  onRecord,
  onClick,
}: PriorityCardProps): JSX.Element {
  const { t } = useTranslation('pagePriorities');

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (!onClick) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onClick();
    }
  };

  const handleRecordClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    onRecord();
  };

  const actionLabel =
    variant === 'updateEvent'
      ? t('priorityCard.actions.update')
      : t('priorityCard.actions.recordNote');

  return (
    <Styled.Card
      data-testid="priority-card"
      data-variant={variant}
      onClick={onClick}
      onKeyDown={onClick ? handleKeyDown : undefined}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      $clickable={!!onClick}
    >
      <Styled.Header>
        <Text.P2>{prompt}</Text.P2>
        {variant === 'updateEvent' && previousUpdate && (
          <Styled.PreviousUpdate>
            <NotesStack size={16} fillColor={colours.neutral[100]} />
            <Styled.PreviousUpdateText>
              {previousUpdate}
            </Styled.PreviousUpdateText>
          </Styled.PreviousUpdate>
        )}
      </Styled.Header>
      <Buttons.SecondaryLight
        $size="medium"
        stretch
        onClick={handleRecordClick}
        data-testid="priority-card-record-button"
      >
        {actionLabel}
      </Buttons.SecondaryLight>
    </Styled.Card>
  );
}
